import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { useLanguage } from './i18n';
import './AnalyticsCharts.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Палитра в стиле приложения (зелёный + акценты)
const COLORS = [
  '#6b8e6e', '#1976d2', '#e74c3c', '#f39c12', '#9b59b6',
  '#16a085', '#2980b9', '#c0392b', '#d35400', '#8e44ad',
  '#27ae60', '#2c3e50', '#7f8c8d', '#bdc3c7'
];

const STATUS_COLORS = {
  'new': '#3498db',
  'assigned': '#9b59b6',
  'in_progress': '#f39c12',
  'completed': '#27ae60',
  'final_completed': '#16a085',
  'rejected': '#e74c3c',
  'closed': '#7f8c8d'
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`;
};

const AC_T = {
  kk: {
    loading: 'Аналитика жүктелуде...',
    loadError: 'Аналитиканы жүктеу қатесі',
    retry: 'Қайталау',
    title: '📊 Аналитика және графиктер',
    periods: { 7: '7 күн', 30: '30 күн', 90: '90 күн', 180: '6 ай' },
    createdInPeriod: 'Кезеңде жасалды',
    completed: 'Аяқталды',
    completionRate: '% орындалу',
    empty: '📭 Таңдалған кезеңде деректер жоқ. Аралықты ұлғайтып көріңіз.',
    trendTitle: '📈 Оқиғалар динамикасы',
    dateLabel: 'Күні',
    createdSeries: 'Жасалды',
    completedSeries: 'Аяқталды',
    categoriesTitle: '🏷 Санаттар бойынша оқиғалар',
    countSeries: 'Саны',
    statusesTitle: '🟢 Мәртебелер бойынша бөлу',
    resolutionTitle: '⏱ Орташа шешу уақыты (сағат) санаттар бойынша',
    hoursSeries: 'Сағат',
    avgTime: 'Орташа уақыт',
    hoursShort: 'сағ'
  },
  ru: {
    loading: 'Загрузка аналитики...',
    loadError: 'Ошибка загрузки аналитики',
    retry: 'Повторить',
    title: '📊 Аналитика и графики',
    periods: { 7: '7 дн.', 30: '30 дн.', 90: '90 дн.', 180: '6 мес.' },
    createdInPeriod: 'Создано за период',
    completed: 'Завершено',
    completionRate: '% выполнения',
    empty: '📭 За выбранный период данных нет. Попробуйте увеличить интервал.',
    trendTitle: '📈 Динамика событий',
    dateLabel: 'Дата',
    createdSeries: 'Создано',
    completedSeries: 'Завершено',
    categoriesTitle: '🏷 События по категориям',
    countSeries: 'Кол-во',
    statusesTitle: '🟢 Распределение по статусам',
    resolutionTitle: '⏱ Среднее время решения (часы) по категориям',
    hoursSeries: 'Часы',
    avgTime: 'Среднее время',
    hoursShort: 'ч'
  }
};

const AnalyticsCharts = () => {
  const { language } = useLanguage();
  const t = AC_T[language];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/dashboard/analytics?days=${period}&lang=${language}`);
      setData(response.data);
    } catch (err) {
      console.error('Analytics load error:', err);
      setError(AC_T[language].loadError);
    } finally {
      setLoading(false);
    }
  }, [period, language]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return (
      <div className="charts-loading" data-testid="charts-loading">
        <div className="charts-spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="charts-error" data-testid="charts-error">
        <p>{error}</p>
        <button onClick={loadAnalytics} className="charts-retry-btn">{t.retry}</button>
      </div>
    );
  }

  if (!data) return null;

  const trendData = data.trend.map(t => ({
    ...t,
    label: formatDate(t.date)
  }));

  const hasData = data.kpi.total_created > 0;

  return (
    <div className="analytics-charts" data-testid="analytics-charts">
      {/* Period selector */}
      <div className="charts-header">
        <h3 className="charts-title">{t.title}</h3>
        <div className="period-selector">
          {[7, 30, 90, 180].map(d => (
            <button
              key={d}
              className={`period-btn ${period === d ? 'active' : ''}`}
              onClick={() => setPeriod(d)}
              data-testid={`period-${d}-btn`}
            >
              {t.periods[d]}
            </button>
          ))}
        </div>
      </div>

      {/* KPI summary */}
      <div className="kpi-row">
        <div className="kpi-card kpi-blue">
          <span className="kpi-label">{t.createdInPeriod}</span>
          <span className="kpi-value" data-testid="kpi-created">{data.kpi.total_created}</span>
        </div>
        <div className="kpi-card kpi-green">
          <span className="kpi-label">{t.completed}</span>
          <span className="kpi-value" data-testid="kpi-completed">{data.kpi.total_completed}</span>
        </div>
        <div className="kpi-card kpi-purple">
          <span className="kpi-label">{t.completionRate}</span>
          <span className="kpi-value" data-testid="kpi-rate">{data.kpi.completion_rate}%</span>
        </div>
      </div>

      {!hasData && (
        <div className="charts-empty" data-testid="charts-empty">
          {t.empty}
        </div>
      )}

      {hasData && (
        <>
          {/* Trend chart */}
          <div className="chart-card" data-testid="chart-trend">
            <h4 className="chart-subtitle">{t.trendTitle}</h4>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#27ae60" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#27ae60" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                <XAxis dataKey="label" fontSize={11} stroke="#666" />
                <YAxis fontSize={11} stroke="#666" allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 13
                  }}
                  labelFormatter={(label) => `${t.dateLabel}: ${label}`}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Area
                  type="monotone"
                  dataKey="created"
                  name={t.createdSeries}
                  stroke="#1976d2"
                  fillOpacity={1}
                  fill="url(#colorCreated)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="completed"
                  name={t.completedSeries}
                  stroke="#27ae60"
                  fillOpacity={1}
                  fill="url(#colorCompleted)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="charts-row">
            {/* Categories bar chart */}
            <div className="chart-card" data-testid="chart-categories">
              <h4 className="chart-subtitle">{t.categoriesTitle}</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={data.categories}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 70, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis type="number" fontSize={11} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    fontSize={11}
                    width={90}
                    stroke="#666"
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 13
                    }}
                  />
                  <Bar dataKey="count" name={t.countSeries} radius={[0, 6, 6, 0]}>
                    {data.categories.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Statuses pie chart */}
            <div className="chart-card" data-testid="chart-statuses">
              <h4 className="chart-subtitle">{t.statusesTitle}</h4>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={data.statuses}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    paddingAngle={3}
                    label={(entry) => `${entry.name}: ${entry.count}`}
                    labelLine={false}
                  >
                    {data.statuses.map((entry, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={STATUS_COLORS[entry.status] || COLORS[idx % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 13
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Resolution time */}
          {data.resolution_times.length > 0 && (
            <div className="chart-card" data-testid="chart-resolution">
              <h4 className="chart-subtitle">{t.resolutionTitle}</h4>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.resolution_times} margin={{ top: 5, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis
                    dataKey="name"
                    fontSize={11}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                    stroke="#666"
                  />
                  <YAxis fontSize={11} stroke="#666" />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 13
                    }}
                    formatter={(val) => [`${val} ${t.hoursShort}`, t.avgTime]}
                  />
                  <Bar dataKey="avg_hours" name={t.hoursSeries} fill="#f39c12" radius={[6, 6, 0, 0]}>
                    {data.resolution_times.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsCharts;
